import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { classNames } from 'support/helpers/generic/generic';
import { WebEDIRowData } from 'components/DataTable/Types';
import { TimestampDateInput } from 'components/Form/TimestampDateInput/TimestampDateInput';
import { useFieldState } from 'components/SchemableForm/hooks/useFieldState';

import { CellProps } from '../../types';
import {
  disableArrowKeys,
  isProcurosAddedValue,
  useCellClassNames,
  useDateConfigurations,
  useIsReadonly,
} from '../cellHelpers';
import { RowStatus } from '../components/RowStatus/RowStatus';
import { InputWrapperCell } from './InputWrapperCell';

export type Props = CellContext<WebEDIRowData, unknown> & CellProps;
export const DateCell = ({ fieldId, column, row, table, isHighlighted, mode }: Props) => {
  const { t } = useTranslation();
  const {
    columnDef: { meta: columnMeta, ...columnDef },
    getIsFirstColumn,
    getIsLastColumn,
  } = column;

  const {
    field: { value: inputValue },
  } = useController({ name: fieldId });

  const { formattedValue } = useDateConfigurations({
    value: inputValue,
    mode,
  });

  const { register } = useFormContext();
  const { service } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const isSubmitting = table.options.meta?.isSubmitting ?? false;
  const isAdded = isProcurosAddedValue(row);
  const readOnly = useIsReadonly({ isAdded, mode, columnMeta, rowIndex: row.index, fieldId });
  const disabled = isSubmitting;
  const fieldState = useFieldState(fieldId, error);

  const cellClassNames = useCellClassNames({
    isLastColumn: getIsLastColumn(),
    isFirstColumn: getIsFirstColumn(),
    row,
    table,
  });

  const inputClassNames = classNames(
    {
      'text-blue-700 focus:text-inherit focus-visible:!ring-blue-500 focus-visible:!border-blue-500':
        fieldState === 'pending-validation',
    },
    cellClassNames,
    'text-start',
    columnMeta?.optionalConfig?.isStatusColumn ? '!pl-10' : undefined,
  );

  return (
    <InputWrapperCell
      fieldId={fieldId}
      isHighlighted={isHighlighted}
      className={cellClassNames}
      meta={columnMeta}
      fieldState={fieldState}
    >
      <TimestampDateInput
        wrapperClassName="size-full"
        inputWrapperClassName="h-full"
        className={classNames(
          'bg-transparent size-full !border-none p-3 !outline-none text-xs !shadow-none rounded-none min-w-[140px]',
          inputClassNames,
        )}
        placeholder={!formattedValue && error?.type === 'required' ? t('common:table.errors.requiredField') : undefined}
        title={formattedValue ? String(formattedValue) : undefined}
        value={formattedValue ?? ''}
        readOnly={readOnly}
        disabled={disabled}
        {...register(fieldId)}
        fieldState={fieldState}
        leftIcon={
          columnMeta?.optionalConfig?.isStatusColumn ? (
            <RowStatus
              statusColumn={columnMeta?.optionalConfig?.quantityField}
              currentPath={fieldId}
              isProcurosAdded={isAdded}
              currentColumn={columnDef && 'accessorKey' in columnDef ? (columnDef.accessorKey as string) : ''}
              tooltips={{
                deleted: t('webedi:inputCell.status.deleted'),
                partial: t('webedi:inputCell.status.partial'),
                complete: t('webedi:inputCell.status.completed'),
              }}
            />
          ) : null
        }
        onKeyDown={disableArrowKeys}
        id={fieldId}
        data-field={fieldId}
        data-testid={fieldId}
        data-1p-ignore
      />
    </InputWrapperCell>
  );
};
